import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import ScrollToTop from '../components/ScrollToTop';
import RouteGuard from '../components/RouteGuard';
import Login from '../pages/Login';
import Register from '../pages/Register';
import HomePage from '../pages/HomePage';
import FeedPourToi from '../pages/FeedPourToi';
import ProjectDetails from '../pages/ProjectDetails';
import ApplyTask from '../pages/ApplyTask';
import AuthorDashboard from '../pages/AuthorDashboard';
import AdminDashboard from '../pages/AdminDashboard';
import Profile from '../pages/Profile';
import ProjectsList from '../pages/ProjectsList';
import ServicesList from '../pages/ServicesList';
import CreateProject from '../pages/CreateProject';
import CreateService from '../pages/CreateService';
import MyApplications from '../pages/MyApplications';
import ReceivedApplications from '../pages/ReceivedApplications';
import Settings from '../pages/Settings';
import NotificationsPage from '../pages/NotificationsPage';
import Navbar from '../components/Navbar';
import { AuthContext } from '../contexts/AuthContext';

// Service Requests
import ServiceDetails from '../pages/ServiceDetails';
import MyServiceRequests from '../pages/MyServiceRequests';
import ServiceRequestDetails from '../pages/ServiceRequestDetails';
import AdminServiceRequests from '../pages/AdminServiceRequests';
import AdminServiceRequestDetail from '../pages/AdminServiceRequestDetail';
import ProServiceRequests from '../pages/ProServiceRequests';
import ProServiceRequestDetail from '../pages/ProServiceRequestDetail';

// User Services (auteur)
import MyServices from '../pages/MyServices';

// Project Finance Management
import ProjectFinanceDashboard from '../pages/ProjectFinanceDashboard';
import ProjectCollaborators from '../pages/ProjectCollaborators';
import ProjectPayments from '../pages/ProjectPayments';
import ProjectObjectives from '../pages/ProjectObjectives';
import ProjectReports from '../pages/ProjectReports';

// Admin Platform Payments & Reports
import AdminPlatformPayments from '../pages/AdminPlatformPayments';
import AdminReports from '../pages/AdminReports';
import AdminAnnouncements from '../pages/admin/AdminAnnouncements';
import AdminWallets from '../pages/AdminWallets';

// Legal Pages
import Terms from '../pages/Terms';
import Privacy from '../pages/Privacy';
import Guide from '../pages/Guide';

// Password Recovery
import ForgotPassword from '../pages/ForgotPassword';
import ResetPassword from '../pages/ResetPassword';
import VerifyEmail from '../pages/VerifyEmail';

// Business Workspace
import BusinessWorkspace from '../pages/BusinessWorkspace';
import BusinessDashboard from '../pages/BusinessDashboard';
import BusinessProducts from '../pages/BusinessProducts';
import BusinessClients from '../pages/BusinessClients';
import BusinessInvoices from '../pages/BusinessInvoices';
import BusinessInvoiceForm from '../pages/BusinessInvoiceForm';
import BusinessInvoiceView from '../pages/BusinessInvoiceView';
import BusinessQuotes from '../pages/BusinessQuotes';
import BusinessQuoteForm from '../pages/BusinessQuoteForm';
import BusinessQuoteView from '../pages/BusinessQuoteView';
import BusinessPayments from '../pages/BusinessPayments';
import BusinessPaymentForm from '../pages/BusinessPaymentForm';
import BusinessExpenses from '../pages/BusinessExpenses';
import BusinessDebts from '../pages/BusinessDebts';
import BusinessPrestations from '../pages/BusinessPrestations';
import BusinessSettings from '../pages/BusinessSettings';
import BusinessReport from '../pages/BusinessReport';
import BusinessShop from '../pages/BusinessShop';
import BusinessShopOrders from '../pages/BusinessShopOrders';
import BusinessRestock from '../pages/BusinessRestock';
import BusinessRestockHistory from '../pages/BusinessRestockHistory';
import BusinessMarketing from '../pages/BusinessMarketing';
import BusinessOffline from '../pages/BusinessOffline';
import BusinessSalesAnalytics from '../pages/BusinessSalesAnalytics';
import BusinessInventories from '../pages/BusinessInventories';
import BusinessInventoryView from '../pages/BusinessInventoryView';
import PlatformWallet from '../pages/PlatformWallet';
import BusinessReturns from '../pages/BusinessReturns';
import BusinessReturnForm from '../pages/BusinessReturnForm';
import BusinessReturnView from '../pages/BusinessReturnView';
import BusinessDeliveryNoteView from '../pages/BusinessDeliveryNoteView';

// Collaboration (Co-management)
import WorkspaceCollaborators from '../pages/WorkspaceCollaborators';
import AcceptInvitation from '../pages/AcceptInvitation';

// Workspace Team (Gestion d'équipe - délégation)
import WorkspaceTeam from '../pages/WorkspaceTeam';
import TeamInvitations from '../pages/TeamInvitations';
import GuestBusinessWorkspace from '../pages/GuestBusinessWorkspace';

// Project Workspace (finances de projet)
import ProjectWorkspace from '../pages/ProjectWorkspace';
import ProjectWorkspaceDashboard from '../pages/ProjectWorkspaceDashboard';
import ProjectWorkspacePermissions from '../pages/ProjectWorkspacePermissions';
import ProjectRestock from '../pages/ProjectRestock';
import ProjectRestockHistory from '../pages/ProjectRestockHistory';

// Subscription Plans
import MySubscription from '../pages/MySubscription';
import SubscriptionRequired from '../pages/SubscriptionRequired';
import AdminSubscriptionPlans from '../pages/AdminSubscriptionPlans';

// Admin Account Management
import AdminAccountManagement from '../pages/AdminAccountManagement';

// Admin Team Management
import AdminTeam from '../pages/AdminTeam';

// Public Shop Pages
import PublicShop from '../pages/PublicShop';
import PublicShopCheckout from '../pages/PublicShopCheckout';
import OrderTracking from '../pages/OrderTracking';
import ShopsDirectory from '../pages/ShopsDirectory';
import ShowcaseDashboard from '../pages/ShowcaseDashboard';
import ShowcaseEditor from '../pages/ShowcaseEditor';
import ShowcasePublic from '../pages/ShowcasePublic';

const AppRoutes = () => {
  const { user, loading } = React.useContext(AuthContext);
  
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <Router>
      {/* Scroll automatique vers le haut à chaque changement de page */}
      <ScrollToTop />
      
      {/* Navbar global - affichée seulement si connecté */}
      {user && <Navbar />}
      
      {/* RouteGuard - Protection automatique des routes basée sur les modules */}
      <RouteGuard>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={!user ? <Login /> : <Navigate to="/" />} />
          <Route path="/register" element={!user ? <Register /> : <Navigate to="/" />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/guide" element={<Guide />} />
          <Route path="/forgot-password" element={!user ? <ForgotPassword /> : <Navigate to="/" />} />
          <Route path="/reset-password" element={!user ? <ResetPassword /> : <Navigate to="/" />} />
          <Route path="/verify-email" element={!user ? <VerifyEmail /> : <Navigate to="/" />} />
          <Route path="/invitation/:token" element={user ? <AcceptInvitation /> : <Navigate to="/login" />} />
          
          {/* Public Shop Routes - No auth required */}
          <Route path="/boutique/:slug" element={<PublicShop />} />
          <Route path="/boutique/:slug/checkout" element={<PublicShopCheckout />} />
          <Route path="/boutiques" element={<ShopsDirectory />} />
          <Route path="/commande/:token" element={<OrderTracking />} />
        <Route path="/commande" element={<OrderTracking />} />
        {/* Showcase public */}
        <Route path="/site/:slug" element={<ShowcasePublic />} />
        <Route path="/site/:slug/:pageSlug" element={<ShowcasePublic />} />
        
        {/* Main Feed */}
        <Route path="/" element={user ? <HomePage /> : <Navigate to="/login" />} />
        <Route path="/feed" element={user ? <FeedPourToi /> : <Navigate to="/login" />} />
        
        {/* Projects */}
        <Route path="/projects" element={<ProjectsList />} />
        <Route path="/project/:id" element={<ProjectDetails />} />
        <Route path="/create-project" element={
          user && (user.role === 'author' || user.role === 'admin') 
            ? <CreateProject /> 
            : <Navigate to="/" />
        } />
        <Route path="/edit-project/:id" element={
          user && (user.role === 'author' || user.role === 'admin') 
            ? <CreateProject /> 
            : <Navigate to="/" />
        } />
        
        {/* Services */}
        <Route path="/services" element={<ServicesList />} />
        <Route path="/services/:id" element={user ? <ServiceDetails /> : <Navigate to="/login" />} />
        <Route path="/create-service" element={
          user && (user.role === 'author' || user.role === 'admin') 
            ? <CreateService /> 
            : <Navigate to="/services" />
        } />
        <Route path="/my-services" element={
          user && (user.role === 'author' || user.role === 'admin') 
            ? <MyServices /> 
            : <Navigate to="/services" />
        } />
        
        {/* Service Requests */}
        <Route path="/my-service-requests" element={user ? <MyServiceRequests /> : <Navigate to="/login" />} />
        <Route path="/my-service-requests/:id" element={user ? <ServiceRequestDetails /> : <Navigate to="/login" />} />
        
        {/* Pro Service Requests - Demandes reçues par les prestataires */}
        <Route path="/pro/service-requests" element={
          user && (user.role === 'author' || user.role === 'admin') 
            ? <ProServiceRequests /> 
            : <Navigate to="/" />
        } />
        <Route path="/pro/service-requests/:id" element={
          user && (user.role === 'author' || user.role === 'admin') 
            ? <ProServiceRequestDetail /> 
            : <Navigate to="/" />
        } />
        
        {/* Project Finance Management */}
        <Route path="/projects/:projectId/finances" element={user ? <ProjectFinanceDashboard /> : <Navigate to="/login" />} />
        <Route path="/projects/:projectId/finances/collaborators" element={user ? <ProjectCollaborators /> : <Navigate to="/login" />} />
        <Route path="/projects/:projectId/finances/payments" element={user ? <ProjectPayments /> : <Navigate to="/login" />} />
        <Route path="/projects/:projectId/finances/objectives" element={user ? <ProjectObjectives /> : <Navigate to="/login" />} />
        <Route path="/projects/:projectId/finances/reports" element={user ? <ProjectReports /> : <Navigate to="/login" />} />

        {/* Project Workspace (gestion financière complète) */}
        <Route path="/projects/:projectId/workspace" element={user ? <ProjectWorkspace /> : <Navigate to="/login" />}>
          <Route index element={<ProjectWorkspaceDashboard />} />
          <Route path="payments" element={<BusinessPayments projectMode={true} />} />
          <Route path="payments/new" element={<BusinessPaymentForm projectMode={true} />} />
          <Route path="expenses" element={<BusinessExpenses projectMode={true} />} />
          <Route path="products" element={<BusinessProducts projectMode={true} />} />
          <Route path="clients" element={<BusinessClients projectMode={true} />} />
          <Route path="invoices" element={<BusinessInvoices projectMode={true} />} />
          <Route path="invoices/new" element={<BusinessInvoiceForm projectMode={true} />} />
          <Route path="invoices/:id" element={<BusinessInvoiceView projectMode={true} />} />
          <Route path="invoices/:id/edit" element={<BusinessInvoiceForm projectMode={true} />} />
          <Route path="prestations" element={<BusinessPrestations projectMode={true} />} />
          <Route path="report" element={<BusinessReport projectMode={true} />} />
          <Route path="restock" element={<ProjectRestock />} />
          <Route path="restock/history" element={<ProjectRestockHistory />} />
          <Route path="sales-analytics" element={<BusinessSalesAnalytics projectMode={true} />} />
          <Route path="permissions" element={<ProjectWorkspacePermissions />} />
        </Route>
        
        {/* Applications */}
        <Route path="/apply/:taskId" element={<ApplyTask />} />
        <Route path="/my-applications" element={user ? <MyApplications /> : <Navigate to="/login" />} />
        <Route path="/received-applications" element={
          user && (user.role === 'author' || user.role === 'admin') 
            ? <ReceivedApplications /> 
            : <Navigate to="/" />
        } />
        
        {/* User Pages */}
        <Route path="/profile" element={user ? <Profile /> : <Navigate to="/login" />} />
        <Route path="/profile/:userId" element={<Profile />} />
        <Route path="/settings" element={user ? <Settings /> : <Navigate to="/login" />} />
       
        <Route path="/notifications" element={user ? <NotificationsPage /> : <Navigate to="/login" />} />
        <Route path="/my-subscription" element={
          user && (user.role === 'author' || user.role === 'admin') 
            ? <MySubscription /> 
            : user 
              ? <SubscriptionRequired />
              : <Navigate to="/login" />
        } />
        
        {/* Author Dashboard */}
        <Route path="/author" element={
          user && (user.role === 'author' || user.role === 'admin') 
            ? <AuthorDashboard /> 
            : <Navigate to="/" />
        } />
        
        {/* Admin Dashboard */}
        <Route path="/admin" element={
          user && user.role === 'admin' 
            ? <AdminDashboard /> 
            : <Navigate to="/" />
        } />
        
        {/* Admin Service Requests */}
        <Route path="/admin/service-requests" element={
          user && user.role === 'admin' 
            ? <AdminServiceRequests /> 
            : <Navigate to="/" />
        } />
        <Route path="/admin/service-requests/:id" element={
          user && user.role === 'admin' 
            ? <AdminServiceRequestDetail /> 
            : <Navigate to="/" />
        } />
        
        {/* Admin Platform Payments */}
        <Route path="/admin/platform-payments" element={
          user && user.role === 'admin' 
            ? <AdminPlatformPayments /> 
            : <Navigate to="/" />
        } />
        
        {/* Admin Reports (Signalements) */}
        <Route path="/admin/reports" element={
          user && user.role === 'admin' 
            ? <AdminReports /> 
            : <Navigate to="/" />
        } />
        
        {/* Admin Announcements */}
        <Route path="/admin/announcements" element={
          user && user.role === 'admin' 
            ? <AdminAnnouncements /> 
            : <Navigate to="/" />
        } />
        
        {/* Admin Wallets */}
        <Route path="/admin/wallets" element={
          user && user.role === 'admin' 
            ? <AdminWallets /> 
            : <Navigate to="/" />
        } />

        {/* Admin Subscription Plans */}
        <Route path="/admin/subscription-plans" element={
          user && user.role === 'admin' 
            ? <AdminSubscriptionPlans /> 
            : <Navigate to="/" />
        } />

        {/* Admin Account Management */}
        <Route path="/admin/account-management" element={
          user && user.role === 'admin' 
            ? <AdminAccountManagement /> 
            : <Navigate to="/" />
        } />

        {/* Admin Team Management */}
        <Route path="/admin/team" element={
          user && user.role === 'admin' 
            ? <AdminTeam /> 
            : <Navigate to="/" />
        } />
        
        {/* Business Workspace - requires active service */}
        <Route path="/workspace" element={
          user && (user.role === 'author' || user.role === 'admin')
            ? <BusinessWorkspace />
            : <Navigate to="/" />
        }>
          <Route index element={<BusinessDashboard />} />
          <Route path="dashboard" element={<BusinessDashboard />} />
          <Route path="products" element={<BusinessProducts />} />
          <Route path="clients" element={<BusinessClients />} />
          <Route path="invoices" element={<BusinessInvoices />} />
          <Route path="invoices/new" element={<BusinessInvoiceForm />} />
          <Route path="invoices/:id" element={<BusinessInvoiceView />} />
          <Route path="invoices/:id/edit" element={<BusinessInvoiceForm />} />
          <Route path="returns" element={<BusinessReturns />} />
          <Route path="returns/new" element={<BusinessReturnForm />} />
          <Route path="returns/:id" element={<BusinessReturnView />} />
          <Route path="delivery-notes/:id" element={<BusinessDeliveryNoteView />} />
          <Route path="quotes" element={<BusinessQuotes />} />
          <Route path="quotes/new" element={<BusinessQuoteForm />} />
          <Route path="quotes/:id" element={<BusinessQuoteView />} />
          <Route path="quotes/:id/edit" element={<BusinessQuoteForm />} />
          <Route path="payments" element={<BusinessPayments />} />
          <Route path="payments/new" element={<BusinessPaymentForm />} />
          <Route path="expenses" element={<BusinessExpenses />} />
          <Route path="debts" element={<BusinessDebts />} />
          <Route path="prestations" element={<BusinessPrestations />} />
          <Route path="report" element={<BusinessReport />} />
          <Route path="restock" element={<BusinessRestock />} />
          <Route path="restock/history" element={<BusinessRestockHistory />} />
          <Route path="inventories" element={<BusinessInventories />} />
          <Route path="inventories/:id" element={<BusinessInventoryView />} />
          <Route path="settings" element={<BusinessSettings />} />
          <Route path="offline" element={<BusinessOffline />} />
          <Route path="collaborators" element={<WorkspaceCollaborators />} />
          <Route path="team" element={<WorkspaceTeam />} />
          <Route path="team-invitations" element={<TeamInvitations />} />
          <Route path="marketing" element={<BusinessMarketing />} />
          <Route path="shop" element={<BusinessShop />} />
          <Route path="shop/orders" element={<BusinessShopOrders />} />
          <Route path="sales-analytics" element={<BusinessSalesAnalytics />} />
          <Route path="wallet" element={<PlatformWallet />} />
        </Route>

        {/* Guest Workspace - Accès au workspace d'un propriétaire (même interface que le workspace personnel) */}
        <Route path="/guest-workspace/:ownerId" element={user ? <GuestBusinessWorkspace /> : <Navigate to="/login" />}>
          <Route index element={<BusinessDashboard />} />
          <Route path="dashboard" element={<BusinessDashboard />} />
          <Route path="products" element={<BusinessProducts />} />
          <Route path="clients" element={<BusinessClients />} />
          <Route path="invoices" element={<BusinessInvoices />} />
          <Route path="invoices/new" element={<BusinessInvoiceForm />} />
          <Route path="invoices/:id" element={<BusinessInvoiceView />} />
          <Route path="invoices/:id/edit" element={<BusinessInvoiceForm />} />
          <Route path="returns" element={<BusinessReturns />} />
          <Route path="returns/new" element={<BusinessReturnForm />} />
          <Route path="returns/:id" element={<BusinessReturnView />} />
          <Route path="delivery-notes/:id" element={<BusinessDeliveryNoteView />} />
          <Route path="quotes" element={<BusinessQuotes />} />
          <Route path="quotes/new" element={<BusinessQuoteForm />} />
          <Route path="quotes/:id" element={<BusinessQuoteView />} />
          <Route path="quotes/:id/edit" element={<BusinessQuoteForm />} />
          <Route path="payments" element={<BusinessPayments />} />
          <Route path="payments/new" element={<BusinessPaymentForm />} />
          <Route path="expenses" element={<BusinessExpenses />} />
          <Route path="debts" element={<BusinessDebts />} />
          <Route path="prestations" element={<BusinessPrestations />} />
          <Route path="report" element={<BusinessReport />} />
          <Route path="restock" element={<BusinessRestock />} />
          <Route path="restock/history" element={<BusinessRestockHistory />} />
          <Route path="inventories" element={<BusinessInventories />} />
          <Route path="inventories/:id" element={<BusinessInventoryView />} />
          <Route path="settings" element={<BusinessSettings />} />
          <Route path="marketing" element={<BusinessMarketing />} />
          <Route path="shop" element={<BusinessShop />} />
          <Route path="shop/orders" element={<BusinessShopOrders />} />
          <Route path="sales-analytics" element={<BusinessSalesAnalytics />} />
        </Route>

        {/* Showcase (auteur/admin) */}
        <Route path="/showcase" element={
          user && (user.role === 'author' || user.role === 'admin')
            ? <ShowcaseDashboard />
            : <Navigate to="/services" />
        } />
        <Route path="/showcase/:id/edit" element={
          user && (user.role === 'author' || user.role === 'admin')
            ? <ShowcaseEditor />
            : <Navigate to="/services" />
        } />
        
        {/* Fallback - 404 */}
        <Route path="*" element={
          <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-blue-100">
            <div className="text-center">
              <span className="text-8xl mb-4 block">🎬</span>
              <h1 className="text-4xl font-bold text-gray-800 mb-4">404</h1>
              <p className="text-gray-600 mb-6">Page non trouvée</p>
              <a href="/" className="px-6 py-3 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition">
                Retour à l'accueil
              </a>
            </div>
          </div>
        } />
        </Routes>
      </RouteGuard>
    </Router>
  );
};

export default AppRoutes;
